import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  BarChart3,
  ChevronRight,
  GraduationCap,
  HelpCircle,
  Home,
  LogOut,
  Mail,
  Menu,
  MessageCircle,
  ShieldCheck,
  Ticket,
  UserCog,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { useCurrentPartner } from "@/hooks/useCurrentPartner";

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  ownerOnly?: boolean;
  // matchers extra: para que /codes/batch/... también active "Códigos"
  match?: (pathname: string) => boolean;
};

const NAV: NavItem[] = [
  { to: "/overview", label: "Resumen", icon: Home },
  { to: "/codes", label: "Códigos", icon: Ticket, match: (p) => p.startsWith("/codes") },
  { to: "/subscriptions", label: "Suscripciones", icon: ShieldCheck },
  { to: "/reports/revenue", label: "Ingresos", icon: BarChart3, match: (p) => p.startsWith("/reports") },
  { to: "/users", label: "Usuarios", icon: UserCog, ownerOnly: true },
];

export function DashboardLayout() {
  const { user, logout } = useAuth();
  const { data: partner } = useCurrentPartner();
  const isOwner = user?.role === "owner";
  const items = NAV.filter((i) => !i.ownerOnly || isOwner);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Cierra el drawer al navegar
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Cierra con Escape y previene scroll del body
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  return (
    <div className="min-h-[100dvh] bg-neutral-50">
      {/* Header móvil con hamburger */}
      <header className="md:hidden sticky top-0 z-40 flex items-center justify-between gap-3 px-4 h-14 bg-white border-b border-neutral-200">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="inline-flex items-center justify-center h-10 w-10 -ml-2 rounded-md text-neutral-700 hover:bg-neutral-100 transition-colors"
          aria-label="Abrir menú"
        >
          <Menu className="h-5 w-5" strokeWidth={2} />
        </button>
        <img
          src="https://raw.githubusercontent.com/Jefferspri/ponos-host-images/main/logo.png"
          alt="NiuFit Partners"
          className="h-6 w-auto"
          style={{ filter: "invert(1) brightness(0.05)" }}
        />
        <div className="w-10" aria-hidden />
      </header>

      <div className="flex">
        {/* Backdrop del drawer móvil */}
        {mobileOpen && (
          <div
            aria-hidden
            onClick={() => setMobileOpen(false)}
            className="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]"
          />
        )}

        {/* Sidebar oscuro editorial (desktop = sticky, móvil = drawer) */}
        <aside
          className={[
            "flex flex-col w-72 bg-ink text-white overflow-hidden",
            // desktop
            "md:sticky md:top-0 md:h-[100dvh] md:w-64 xl:w-72 md:translate-x-0",
            // mobile drawer
            "fixed inset-y-0 left-0 z-50 h-[100dvh] transform transition-transform duration-300",
            mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
          ].join(" ")}
        >
          {/* Trama de líneas verticales muy sutiles */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(90deg, rgba(255,255,255,0.6) 0 1px, transparent 1px 96px)",
            }}
          />
          {/* Halo plateado sutil arriba */}
          <div
            aria-hidden
            className="absolute -top-32 -left-24 h-72 w-72 rounded-full blur-[100px] opacity-[0.14] pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, hsl(var(--silver-hi)) 0%, transparent 65%)",
            }}
          />

          {/* Marca + partner meta + cerrar (mobile) */}
          <header className="relative z-10 px-6 pt-7 pb-6">
            <div className="flex items-start justify-between gap-3">
              <img
                src="https://raw.githubusercontent.com/Jefferspri/ponos-host-images/main/logo.png"
                alt="NiuFit Partners"
                className="h-7 w-auto"
              />
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="md:hidden inline-flex items-center justify-center h-8 w-8 -mr-1 rounded-md text-white/60 hover:bg-white/10 hover:text-white transition-colors"
                aria-label="Cerrar menú"
              >
                <X className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>
            <div className="mt-5 space-y-1">
              <div className="text-[15px] font-medium tracking-tight truncate">
                {partner?.name ?? "Cargando…"}
              </div>
              {partner && (
                <div className="text-[10px] font-mono-num uppercase tracking-[0.18em] text-white/40 flex items-center gap-2">
                  <span>{partner.type}</span>
                  <span className="text-white/20">/</span>
                  <span>{partner.currency}</span>
                  <span className="text-white/20">/</span>
                  <span className="truncate">{partner.timezone}</span>
                </div>
              )}
            </div>
          </header>

          {/* Divider fino */}
          <div className="relative z-10 mx-6 h-px bg-white/[0.06]" />

          {/* Navegación */}
          <nav className="relative z-10 flex-1 px-3 py-6 overflow-y-auto">
            <div className="px-3 mb-3 text-[10px] font-mono-num uppercase tracking-[0.22em] text-white/30">
              Panel
            </div>
            <NavRail items={items} />
          </nav>

          {/* Nav secundaria — recursos y soporte */}
          <div className="relative z-10 px-3 pb-3 space-y-0.5">
            <TutorialsLink />
            <HelpButton />
          </div>

          {/* Footer usuario */}
          <footer className="relative z-10 px-4 pt-4 pb-5 border-t border-white/[0.06]">
            <UserBlock name={user?.name ?? ""} email={user?.email ?? ""} role={user?.role ?? ""} onLogout={logout} />
          </footer>
        </aside>

        {/* Contenido */}
        <main className="flex-1 min-h-[100dvh] min-w-0">
          <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

/* ─────────────── NavRail con indicador plateado deslizante ─────────────── */

function NavRail({ items }: { items: NavItem[] }) {
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const linkRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const [pos, setPos] = useState<{ top: number; height: number } | null>(null);

  const activeIndex = items.findIndex((i) =>
    i.match ? i.match(location.pathname) : location.pathname === i.to
  );

  useEffect(() => {
    const update = () => {
      const el = linkRefs.current[activeIndex];
      const cont = containerRef.current;
      if (!el || !cont) return;
      const c = cont.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      setPos({ top: r.top - c.top, height: r.height });
    };
    update();
    window.addEventListener("resize", update);
    const t = window.setTimeout(update, 300);
    return () => {
      window.removeEventListener("resize", update);
      window.clearTimeout(t);
    };
  }, [activeIndex, items.length]);

  return (
    <div ref={containerRef} className="relative flex flex-col gap-0.5">
      {/* Indicador plateado que se desliza */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-0 w-[3px] rounded-full bg-silver-metal transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]"
        style={{
          top: pos?.top ?? 0,
          height: pos?.height ?? 0,
          opacity: pos ? 1 : 0,
        }}
      />
      {items.map((item, i) => {
        const Icon = item.icon;
        const isActive = i === activeIndex;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            ref={(el) => { linkRefs.current[i] = el; }}
            className={[
              "group relative flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors",
              isActive
                ? "text-white bg-white/[0.04]"
                : "text-white/55 hover:text-white hover:bg-white/[0.03]",
            ].join(" ")}
          >
            <Icon
              className={[
                "h-[18px] w-[18px] transition-colors",
                isActive ? "text-silver-hi" : "text-white/40 group-hover:text-white/70",
              ].join(" ")}
              strokeWidth={1.75}
            />
            <span className="font-medium tracking-tight">{item.label}</span>
            <ChevronRight
              className={[
                "ml-auto h-3.5 w-3.5 transition-all",
                isActive
                  ? "opacity-60 translate-x-0"
                  : "opacity-0 -translate-x-1 group-hover:opacity-30 group-hover:translate-x-0",
              ].join(" ")}
              strokeWidth={2}
            />
          </NavLink>
        );
      })}
    </div>
  );
}

/* ─────────────── Tutoriales link — navega a la página de recursos ─────────────── */

function TutorialsLink() {
  return (
    <NavLink
      to="/tutorials"
      className={({ isActive }) =>
        [
          "group w-full flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors",
          isActive
            ? "text-white bg-white/[0.04]"
            : "text-white/55 hover:text-white hover:bg-white/[0.03]",
        ].join(" ")
      }
    >
      {({ isActive }) => (
        <>
          <GraduationCap
            className={[
              "h-[18px] w-[18px] transition-colors",
              isActive ? "text-silver-hi" : "text-white/40 group-hover:text-white/70",
            ].join(" ")}
            strokeWidth={1.75}
          />
          <span className="font-medium tracking-tight">Tutoriales</span>
        </>
      )}
    </NavLink>
  );
}

/* ─────────────── Help button — abre dialog con contactos ─────────────── */

const SUPPORT_EMAIL = "jefferespri@gmail.com";
const SUPPORT_WHATSAPP_LABEL = "+51 944 169 616";
// wa.me usa el número internacional sin '+' ni espacios
const SUPPORT_WHATSAPP_URL = "https://wa.me/51944169616";

function HelpButton() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="group w-full flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-white/55 hover:text-white hover:bg-white/[0.03] transition-colors"
        >
          <HelpCircle className="h-[18px] w-[18px] text-white/40 group-hover:text-white/70 transition-colors" strokeWidth={1.75} />
          <span className="font-medium tracking-tight">Ayuda</span>
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold tracking-tight">
            ¿Necesitas ayuda?
          </DialogTitle>
          <DialogDescription>
            Escríbenos o llámanos. Te respondemos lo antes posible.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="group flex items-center gap-3 rounded-md border border-neutral-200 bg-white p-3 hover:border-neutral-300 hover:bg-neutral-50 transition-colors"
          >
            <div className="rounded-full bg-neutral-100 p-2 group-hover:bg-white transition-colors">
              <Mail className="h-4 w-4 text-neutral-700" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.14em] text-neutral-500 font-medium">
                Email
              </div>
              <div className="text-sm text-neutral-900 font-mono-num truncate">
                {SUPPORT_EMAIL}
              </div>
            </div>
          </a>

          <a
            href={SUPPORT_WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 rounded-md border border-neutral-200 bg-white p-3 hover:border-emerald-200 hover:bg-emerald-50/40 transition-colors"
          >
            <div className="rounded-full bg-emerald-50 p-2 border border-emerald-100 group-hover:bg-white transition-colors">
              <MessageCircle className="h-4 w-4 text-emerald-700" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.14em] text-neutral-500 font-medium">
                WhatsApp
              </div>
              <div className="text-sm text-neutral-900 font-mono-num">
                {SUPPORT_WHATSAPP_LABEL}
              </div>
            </div>
          </a>
        </div>

        <DialogFooter className="mt-4">
          <DialogClose className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors">
            Cerrar
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────── User block (footer sidebar) ─────────────── */

function UserBlock({
  name,
  email,
  role,
  onLogout,
}: {
  name: string;
  email: string;
  role: string;
  onLogout: () => void;
}) {
  const initial = (name || email || "P").charAt(0).toUpperCase();
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 px-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-silver-metal text-black font-semibold text-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]">
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium tracking-tight truncate">{name}</div>
          <div className="text-[11px] text-white/40 truncate font-mono-num">{email}</div>
        </div>
      </div>
      <div className="flex items-center justify-between px-2">
        <span className="text-[10px] font-mono-num uppercase tracking-[0.18em] text-white/35">
          {role}
        </span>
        <button
          onClick={onLogout}
          className="group inline-flex items-center gap-1.5 text-[11px] font-mono-num uppercase tracking-[0.14em] text-white/45 hover:text-white transition-colors"
        >
          <LogOut className="h-3 w-3 transition-transform group-hover:translate-x-0.5" strokeWidth={2.25} />
          Salir
        </button>
      </div>
    </div>
  );
}
